// Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { useEffect, useState, useRef } from 'react'
import { fetchDailyPoolOverviewCTOs } from '@react/fetchers/PoolFetchers'
import { fetchLytPoolReserve } from '@react/lyt/LytFetchers'
import { erc20WeiToCommaDecString } from '@shared/conversions'
import { fetchOptimizedCurrentAPYForSierra } from '@react/fetchers/apyOptimizedFetcher'
import { useOtDispatch, useOtSelector, useOtChainState } from '@react/redux/store'
import {
  fetchCurrentAPY,
  fetchAPYGraphData,
  fetchReserves,
  selectCurrentAPY,
  selectAPYGraphData,
  selectAPYLoading,
  selectAPYError,
  selectAPYLastUpdated,
  selectReserves,
  selectReservesWithRates,
  selectReservesLoading,
  selectReservesError,
  selectReservesLastUpdated,
  shouldRefreshAPY,
  setBackgroundRefreshing,
} from '@react/redux/apySlice'

interface UseCurrentAPYOptions {
  serverApiBaseUrl?: string
  restAPIKey?: string
  poolAddr?: string
  enabled?: boolean
  accountAddr?: string
}

interface UseCurrentAPYReturn {
  currentAPY: string | null
  isLoading: boolean
  error: Error | null
}

interface UseAPYDataOptions {
  enabled?: boolean
  timeFilter?: '1M' | '3M' | '6M' | '1Y'
  refreshInterval?: number
}

interface UseTransparencyOptions {
  enabled?: boolean
  timeFilter?: '1M' | '3M' | '6M' | '1Y'
  refreshInterval?: number
  includeReserves?: boolean
}

interface BackgroundRefreshOptions {
  enabled?: boolean
  apyRefreshInterval?: number
  graphRefreshInterval?: number
  retryOnError?: boolean
}

const aggregateToWeekly = (dailyData: Array<{ timestamp: number; value: number }>) => {
  if (dailyData.length === 0) return []

  const weeklyData: Array<{ timestamp: number; value: number }> = []

  for (let i = 0; i < dailyData.length; i += 7) {
    const weekData = dailyData.slice(i, i + 7).filter((item) => !isNaN(item.value) && item.value !== null)
    if (weekData.length > 0) {
      const weekTimestamp = weekData[Math.floor(weekData.length / 2)].timestamp
      const validValues = weekData.filter((item) => item.value !== 0 || weekData.every((w) => w.value === 0))
      const weekValue =
        validValues.length > 0
          ? validValues.reduce((sum, item) => sum + item.value, 0) / validValues.length
          : weekData.reduce((sum, item) => sum + item.value, 0) / weekData.length

      weeklyData.push({
        timestamp: weekTimestamp,
        value: weekValue,
      })
    }
  }
  return weeklyData
}

export function useCurrentAPY({
  serverApiBaseUrl,
  restAPIKey,
  poolAddr,
  enabled = true,
  accountAddr,
}: UseCurrentAPYOptions): UseCurrentAPYReturn {
  const [currentAPY, setCurrentAPY] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  useEffect(() => {
    if (!enabled || !serverApiBaseUrl || !restAPIKey || !poolAddr) {
      return
    }

    const calculateCurrentAPY = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const isSierraApp = location.hostname.includes('sierra')

        if (isSierraApp) {
          const optimizedAPY = await fetchOptimizedCurrentAPYForSierra(
            serverApiBaseUrl,
            restAPIKey,
            poolAddr,
            accountAddr
          )
          if (optimizedAPY !== null) {
            setCurrentAPY(optimizedAPY)
            return
          }
        }

        const reserveAllocation = await fetchLytPoolReserve(serverApiBaseUrl, restAPIKey, poolAddr)
        const reserves = reserveAllocation.reserves

        if (!reserves || reserves.length === 0) {
          setCurrentAPY(null)
          return
        }

        const start = new Date()
        start.setFullYear(start.getFullYear() - 1)
        const startDayStr = start.toISOString().slice(0, 10)

        const allPromises = reserves.map((reserve) =>
          fetchDailyPoolOverviewCTOs(serverApiBaseUrl, restAPIKey, reserve.poolAddr, startDayStr, undefined)
            .then((result) =>
              result.map((d: any) => ({
                assetValue: parseFloat(erc20WeiToCommaDecString(reserve.assetBalance)),
                timestamp: d.timestamp,
                apy: d.indicativeInterestRate
                  ? Number(d.indicativeInterestRate) / 100
                  : d.interestRate
                    ? Number(d.interestRate) / 100
                    : 0,
              }))
            )
            .catch(() => [])
        )

        const allAssetHistoricalData = await Promise.all(allPromises)
        const flatData = allAssetHistoricalData.flat()

        const groupedByTimestamp = flatData.reduce(
          (acc, item) => {
            if (!acc[item.timestamp]) acc[item.timestamp] = []
            acc[item.timestamp].push(item)
            return acc
          },
          {} as Record<number, Array<{ assetValue: number; timestamp: number; apy: number }>>
        )

        const weightedAverageData = Object.entries(groupedByTimestamp)
          .map(([timestamp, assets]) => {
            const totalValue = assets.reduce((sum, asset) => sum + asset.assetValue, 0)
            const weightedSum = assets.reduce((sum, asset) => sum + asset.apy * asset.assetValue, 0)
            const weightedAverage = totalValue > 0 ? weightedSum / totalValue : 0

            return { timestamp: parseInt(timestamp), value: weightedAverage }
          })
          .sort((a, b) => a.timestamp - b.timestamp)

        const weeklyData = aggregateToWeekly(weightedAverageData)

        if (weeklyData.length > 0) {
          const latestAPY = weeklyData[weeklyData.length - 1].value
          setCurrentAPY(latestAPY.toFixed(2))
        } else {
          setCurrentAPY(null)
        }
      } catch (err) {
        console.error('Error calculating current APY:', err)
        setError(err instanceof Error ? err : new Error('Failed to calculate APY'))
        setCurrentAPY(null)
      } finally {
        setIsLoading(false)
      }
    }

    calculateCurrentAPY()
  }, [enabled, serverApiBaseUrl, restAPIKey, poolAddr, accountAddr])

  return {
    currentAPY,
    isLoading,
    error,
  }
}

export const useAPYData = (options: UseAPYDataOptions = {}) => {
  const { enabled = true, timeFilter = '1Y', refreshInterval = 5 * 60 * 1000 } = options

  const dispatch = useOtDispatch()
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl, lytPoolAddr },
    userConnection: { accountAddr },
  } = useOtChainState()

  const currentAPY = useOtSelector(selectCurrentAPY)
  const graphData = useOtSelector((state) => selectAPYGraphData(state, timeFilter))
  const isLoading = useOtSelector(selectAPYLoading)
  const error = useOtSelector(selectAPYError)
  const lastUpdated = useOtSelector(selectAPYLastUpdated)

  useEffect(() => {
    if (!enabled || !serverApiBaseUrl || !restAPIKey || !lytPoolAddr) {
      return
    }

    const needsRefresh = shouldRefreshAPY(lastUpdated, refreshInterval)

    if (needsRefresh) {
      dispatch(
        fetchCurrentAPY({
          serverApiBaseUrl,
          restAPIKey,
          poolAddr: lytPoolAddr,
          accountAddr,
        })
      )
    }
  }, [enabled, serverApiBaseUrl, restAPIKey, lytPoolAddr, accountAddr, dispatch, lastUpdated, refreshInterval])

  useEffect(() => {
    if (!enabled || !serverApiBaseUrl || !restAPIKey || !lytPoolAddr) {
      return
    }

    const hasGraphData = graphData && graphData.length > 0
    const needsRefresh = shouldRefreshAPY(lastUpdated, refreshInterval)

    if (!hasGraphData || needsRefresh) {
      dispatch(
        fetchAPYGraphData({
          serverApiBaseUrl,
          restAPIKey,
          poolAddr: lytPoolAddr,
          timeFilter,
          accountAddr,
        })
      )
    }
  }, [
    enabled,
    serverApiBaseUrl,
    restAPIKey,
    lytPoolAddr,
    timeFilter,
    accountAddr,
    dispatch,
    graphData,
    lastUpdated,
    refreshInterval,
  ])

  return {
    currentAPY,
    graphData,
    isLoading,
    error,
    lastUpdated,
  }
}

export const useCurrentAPYOnly = (options: { enabled?: boolean } = {}) => {
  const { enabled = true } = options

  const dispatch = useOtDispatch()
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl, lytPoolAddr },
    userConnection: { accountAddr },
  } = useOtChainState()

  const currentAPY = useOtSelector(selectCurrentAPY)
  const isLoading = useOtSelector(selectAPYLoading)
  const error = useOtSelector(selectAPYError)
  const lastUpdated = useOtSelector(selectAPYLastUpdated)

  useEffect(() => {
    if (!enabled || !serverApiBaseUrl || !restAPIKey || !lytPoolAddr) {
      return
    }

    const needsRefresh = shouldRefreshAPY(lastUpdated)

    if (needsRefresh) {
      dispatch(
        fetchCurrentAPY({
          serverApiBaseUrl,
          restAPIKey,
          poolAddr: lytPoolAddr,
          accountAddr,
        })
      )
    }
  }, [enabled, serverApiBaseUrl, restAPIKey, lytPoolAddr, accountAddr, dispatch, lastUpdated])

  return {
    currentAPY,
    isLoading,
    error,
  }
}

export const useBackgroundAPYRefresh = (options: BackgroundRefreshOptions = {}) => {
  const {
    enabled = true,
    apyRefreshInterval = 5 * 60 * 1000,
    graphRefreshInterval = 15 * 60 * 1000,
    retryOnError = true,
  } = options

  const dispatch = useOtDispatch()
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl, lytPoolAddr },
    userConnection: { accountAddr },
  } = useOtChainState()

  const apyIntervalRef = useRef<NodeJS.Timeout>()
  const graphIntervalRef = useRef<NodeJS.Timeout>()
  const retryTimeoutRef = useRef<NodeJS.Timeout>()

  const refreshAPY = async (isRetry = false) => {
    if (!serverApiBaseUrl || !restAPIKey || !lytPoolAddr) {
      return
    }

    try {
      if (!isRetry) {
        dispatch(setBackgroundRefreshing(true))
      }

      await dispatch(
        fetchCurrentAPY({
          serverApiBaseUrl,
          restAPIKey,
          poolAddr: lytPoolAddr,
          accountAddr,
        })
      ).unwrap()
    } catch (error) {
      console.warn('Background APY refresh failed:', error)

      if (retryOnError && !isRetry) {
        retryTimeoutRef.current = setTimeout(() => {
          refreshAPY(true)
        }, 30000)
      }
    } finally {
      if (!isRetry) {
        dispatch(setBackgroundRefreshing(false))
      }
    }
  }

  const refreshGraphData = async (timeFilter: '1M' | '3M' | '6M' | '1Y' = '1Y', isRetry = false) => {
    if (!serverApiBaseUrl || !restAPIKey || !lytPoolAddr) {
      return
    }

    try {
      await dispatch(
        fetchAPYGraphData({
          serverApiBaseUrl,
          restAPIKey,
          poolAddr: lytPoolAddr,
          timeFilter,
          accountAddr,
        })
      ).unwrap()
    } catch (error) {
      console.warn('Background graph refresh failed:', error)

      if (retryOnError && !isRetry) {
        retryTimeoutRef.current = setTimeout(() => {
          refreshGraphData(timeFilter, true)
        }, 60000)
      }
    }
  }

  useEffect(() => {
    if (!enabled) {
      return
    }

    apyIntervalRef.current = setInterval(() => {
      refreshAPY()
    }, apyRefreshInterval)

    graphIntervalRef.current = setInterval(() => {
      refreshGraphData('1Y')
      refreshGraphData('6M')
      refreshGraphData('3M')
      refreshGraphData('1M')
    }, graphRefreshInterval)

    return () => {
      if (apyIntervalRef.current) clearInterval(apyIntervalRef.current)
      if (graphIntervalRef.current) clearInterval(graphIntervalRef.current)
      if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current)
    }
  }, [enabled, apyRefreshInterval, graphRefreshInterval, serverApiBaseUrl, restAPIKey, lytPoolAddr, accountAddr])

  return {
    refreshAPY: () => refreshAPY(),
    refreshGraphData: (timeFilter: '1M' | '3M' | '6M' | '1Y' = '1Y') => refreshGraphData(timeFilter),
  }
}

export const useTransparency = (options: UseTransparencyOptions = {}) => {
  const { enabled = true, timeFilter = '1Y', refreshInterval = 5 * 60 * 1000, includeReserves = true } = options

  const dispatch = useOtDispatch()
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl, lytPoolAddr },
    userConnection: { accountAddr },
  } = useOtChainState()

  const currentAPY = useOtSelector(selectCurrentAPY)
  const graphData = useOtSelector((state) => selectAPYGraphData(state, timeFilter))
  const reserves = useOtSelector(selectReserves)
  const reservesWithRates = useOtSelector(selectReservesWithRates)
  const isLoading = useOtSelector(selectAPYLoading)
  const error = useOtSelector(selectAPYError)
  const lastUpdated = useOtSelector(selectAPYLastUpdated)
  const reservesLastUpdated = useOtSelector(selectReservesLastUpdated)

  useEffect(() => {
    if (!enabled || !serverApiBaseUrl || !restAPIKey || !lytPoolAddr) {
      return
    }

    const needsRefresh = shouldRefreshAPY(lastUpdated, refreshInterval)

    if (needsRefresh) {
      dispatch(
        fetchCurrentAPY({
          serverApiBaseUrl,
          restAPIKey,
          poolAddr: lytPoolAddr,
          accountAddr,
        })
      )
    }
  }, [enabled, serverApiBaseUrl, restAPIKey, lytPoolAddr, accountAddr, dispatch, lastUpdated, refreshInterval])

  useEffect(() => {
    if (!enabled || !serverApiBaseUrl || !restAPIKey || !lytPoolAddr || !includeReserves) {
      return
    }

    const needsReservesRefresh = shouldRefreshAPY(reservesLastUpdated, refreshInterval)

    if (needsReservesRefresh) {
      dispatch(
        fetchReserves({
          serverApiBaseUrl,
          restAPIKey,
          poolAddr: lytPoolAddr,
        })
      )
    }
  }, [
    enabled,
    serverApiBaseUrl,
    restAPIKey,
    lytPoolAddr,
    dispatch,
    reservesLastUpdated,
    refreshInterval,
    includeReserves,
  ])

  useEffect(() => {
    if (!enabled || !serverApiBaseUrl || !restAPIKey || !lytPoolAddr) {
      return
    }

    const hasGraphData = graphData && graphData.length > 0

    if (!hasGraphData) {
      dispatch(
        fetchAPYGraphData({
          serverApiBaseUrl,
          restAPIKey,
          poolAddr: lytPoolAddr,
          timeFilter,
          accountAddr,
        })
      )
    }
  }, [enabled, serverApiBaseUrl, restAPIKey, lytPoolAddr, timeFilter, accountAddr, dispatch, graphData])

  return {
    currentAPY,
    graphData,
    reserves,
    reservesWithRates,
    isLoading,
    error,
    lastUpdated,
  }
}

export const useReserves = (options: { enabled?: boolean; refreshInterval?: number } = {}) => {
  const { enabled = true, refreshInterval = 5 * 60 * 1000 } = options

  const dispatch = useOtDispatch()
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl, lytPoolAddr },
  } = useOtChainState()

  const reserves = useOtSelector(selectReserves)
  const reservesWithRates = useOtSelector(selectReservesWithRates)
  const isLoading = useOtSelector(selectReservesLoading)
  const error = useOtSelector(selectReservesError)
  const reservesLastUpdated = useOtSelector(selectReservesLastUpdated)

  useEffect(() => {
    if (!enabled || !serverApiBaseUrl || !restAPIKey || !lytPoolAddr) {
      return
    }

    const needsReservesRefresh = shouldRefreshAPY(reservesLastUpdated, refreshInterval)

    if (needsReservesRefresh) {
      dispatch(
        fetchReserves({
          serverApiBaseUrl,
          restAPIKey,
          poolAddr: lytPoolAddr,
        })
      )
    }
  }, [enabled, serverApiBaseUrl, restAPIKey, lytPoolAddr, dispatch, reservesLastUpdated, refreshInterval])

  return {
    reserves,
    reservesWithRates,
    isLoading,
    error,
  }
}
